import React, { CSSProperties, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useServiceHook from "@hooks/useServiceHook";

import { NavBar } from "@components/navigation/NavBar/NavBar";
import { IPanelItem, PanelItem } from "@components/panels/PanelItems";
import Label from "@components/inputs/text/Label";
import DropDownMenu from "@components/inputs/DropDownMenu";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import PriceBreakdownComponent from "./PriceBreakdownComponent";
import VehicleQuoteUsageIndicator from "./VehicleQuoteUsageIndicator";
import {
  SelectedVehicleAction,
  SelectedVehicleState,
} from "./selectedVehicleReducer";
import {
  QuoteBuilderService,
  IFilterOption,
  IFilterOptions,
  IPricingRecord,
} from "@services/QuoteBuilder/QuoteBuilderService";
import { CustomerPotentialDetail } from "./customerPotentialDetailReducer";
import ModalContainer from '@components/modal/Container';
import CircularSpinner from "@components/spinners/Circular/Circular";
import { PricingRecordAction } from "./pricingRecordReducer";
import { FilterOptionsAction } from "./filterOptionReducer";
import { ErrorAction, ErrorState } from "./errorReducer";
import { InfoBox } from "@components/cards/messageBox";
import Warnings from "@utils/Warnings";
import { MasterSheetFilter } from "./selectedVehicleReducer";

const ForegroundLoadingSpinner = () => (
  <ModalContainer stylesCfg={{ windowTransparency: true }} >
    <LoadingSpinner />
  </ModalContainer>
)

const LoadingSpinner = () => (
  <div data-testid="loading-spinner" style={{ display: 'flex', justifyContent: 'center', margin: '15vh' }}>
    {<CircularSpinner />}
  </div>
)

const ResetButton = ({ onClick, testId }: { onClick: () => void; testId: string }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      background: "none",
      border: "1px solid #ccc",
      borderRadius: "4px",
      padding: "4px 8px",
      cursor: "pointer",
      fontSize: "12px",
      color: "#666"
    }}
    data-testid={testId}
  >
    Reset
  </button>
)

type Props = {
  customerPotentialDetailState: CustomerPotentialDetail;
  filterState: SelectedVehicleState;
  pricingRecordState: IPricingRecord | null;
  filterOptionsState: IFilterOptions | null;
  filterDispatch: React.Dispatch<SelectedVehicleAction>;
  pricingRecordDispatch: React.Dispatch<PricingRecordAction>;
  filterOptionsDispatch: React.Dispatch<FilterOptionsAction>;
  errorState: ErrorState;
  errorDispatch: React.Dispatch<ErrorAction>;
  navigateTarget: string;
  navigateLandingPageTarget: string;
  foQuoting21Enabled: boolean;
  vehicleSpecifierQuotingEnabled: boolean;
};

// General helper function to get the details of a pricing line item by ID.
// Typically used for displaying the pricing plan name.
const getLineItemByLabelCode = (
  pricingRecord: IPricingRecord,
  lineItemLabelCode: string
) => {
  return pricingRecord?.pricing_record_line_items?.find(
    (item) => item.line_item_label_code === lineItemLabelCode
  );
};

export default function PlanDetailsView({
  customerPotentialDetailState,
  filterState,
  pricingRecordState,
  filterOptionsState,
  filterDispatch,
  pricingRecordDispatch,
  filterOptionsDispatch,
  errorState,
  errorDispatch,
  navigateTarget,
  navigateLandingPageTarget,
  foQuoting21Enabled,
  vehicleSpecifierQuotingEnabled,
}: Props) {
  const navigate = useNavigate();

  // Allow service to run on page load to fetch initial options
  const [allowServiceRun, setAllowServiceRun] = useState<boolean>(true);

  const customerDetails: IPanelItem[] = [
    {
      header: "Customer Name",
      headerTextStyleCfg: { bold: true },
      value: customerPotentialDetailState.customerName,
    },
    {
      header: "Customer Email",
      headerTextStyleCfg: { bold: true },
      value: customerPotentialDetailState.customerEmail,
    },
    {
      header: "Potential ID",
      headerTextStyleCfg: { bold: true },
      value: customerPotentialDetailState.potentialId,
    },
    {
      header: "Plan Type",
      headerTextStyleCfg: { bold: true },
      value: customerPotentialDetailState.planType,
    },
    {
      header: "Location",
      headerTextStyleCfg: { bold: true },
      value: customerPotentialDetailState.location,
    },
  ];

  const style: CSSProperties = {
    display: "flex",
    flexDirection: "column",
  };

  // True when all required filters have been selected.
  // When IC_BP_QUOTING_ENABLED is off, always true (IC/BP not required).
  // When on: FT is always true (IC/BP not in validFilters); FO/FO Used requires both selected.
  // TODO: Balloon payment is temporarily hidden. Re-add the balloon payment condition below when re-enabling:
  //   && (!filterState?.validFilters.includes(MasterSheetFilter.BALLOON_PAYMENT) || !!filterState?.selectedBalloonPayment)
  const allRequiredFiltersSelected = !foQuoting21Enabled || (
    (!filterState?.validFilters.includes(MasterSheetFilter.INITIAL_CONTRIBUTION) || !!filterState?.selectedInitialContribution)
  );

  /**
   * Handle input changes
   */
  // This service hook is used to fetch the master pricing plan options based on
  // the customer details and selected vehicle attributes. It gets triggered
  // whenever there are changes in the selection.
  const [loading, error, errMessage, masterPricingPlanOptions] = useServiceHook(
    QuoteBuilderService.getMasterPricingPlan,
    [
      {
        country: filterState?.selectedCountry,
        state: filterState?.selectedState,
        plan: filterState?.selectedPlan,
        vehicle_condition: filterState?.selectedVehicleCondition,
        term_length: filterState?.selectedContractTerm || null,
        make: filterState?.selectedVehicleMake || null,
        model: filterState?.selectedVehicleModel || null,
        model_year: filterState?.selectedVehicleModelYear || null,
        weekly_distance_allowance_id: filterState?.selectedDistanceAllowance || null,
        initial_contribution_id: filterState?.selectedInitialContribution || ( foQuoting21Enabled ? null : '0'),
        // TODO: Balloon payment is temporarily hidden; always send '0' as default.
        // When re-enabling, restore: filterState?.selectedBalloonPayment || (foQuoting21Enabled ? null : '0')
        balloon_payment_id: '0',
        // When per-vehicle quoting is on, null until selected: the server treats an absent
        // specifier as '0' (generic plan) when generating a record, and an eager '0' filter
        // would hide POFO-only vehicles from the upstream make/model/year dropdowns.
        // When off, '0' is sent from the first request so only generic plans are ever
        // matched — a per-vehicle plan in the catalog would otherwise leave the combination
        // unresolvable with no dropdown to resolve it.
        vehicle_specifier: filterState?.selectedVehicleSpecifier
          || (vehicleSpecifierQuotingEnabled ? null : '0')
      },
    ],
    (allowServiceRun && filterState !== null && pricingRecordState == null),
    [
      filterState?.selectedContractTerm,
      filterState?.selectedVehicleMake,
      filterState?.selectedVehicleModel,
      filterState?.selectedVehicleModelYear,
      filterState?.selectedDistanceAllowance,
      filterState?.selectedInitialContribution,
      filterState?.selectedBalloonPayment,
      filterState?.selectedVehicleSpecifier,
      // The flag resolves asynchronously and decides whether the specifier is sent as
      // null or '0', so a change to it has to re-fetch the options it filters.
      vehicleSpecifierQuotingEnabled
    ],
    undefined,
    [
      customerPotentialDetailState.potentialId
    ]
  );

  // Redirect to landing page if filterState is null (i.e. user accessed this page directly)
  useEffect(() => {
    if (filterState === null) {
      navigate(navigateLandingPageTarget);
    }
  }, [filterState, navigateLandingPageTarget, navigate]);

  // Update available options when API response is received
  useEffect(() => {
    if (masterPricingPlanOptions && !loading && !error) {
      // Only set the pricing record once all required filters are selected
      // (IC/BP for FO/FO Used when flag is on). Filter options always update
      // so dropdowns like term length, make, model etc. continue to populate.
      if (allRequiredFiltersSelected) {
        pricingRecordDispatch({
          type: "SET_PRICING_RECORD",
          payload: masterPricingPlanOptions.pricing_record,
        });
      }

      filterOptionsDispatch({
        type: "SET_FILTER_OPTIONS",
        payload: masterPricingPlanOptions.filter_option,
      });

      // Set errors from API response (empty array if no errors)
      errorDispatch({
        type: 'SET_ERRORS',
        payload: masterPricingPlanOptions.errors
      });
    }
  }, [masterPricingPlanOptions, loading, error, allRequiredFiltersSelected, pricingRecordDispatch, filterOptionsDispatch, errorDispatch]);

  /**
   * on Select functions
   */
  const onSelectContractTerm = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedValue = event.currentTarget.value;
    if (selectedValue === "") {
      resetFilter("contractTerm");
    } else {
      filterDispatch({
        type: "USER_SELECTS_CONTRACT_TERM",
        payload: {
          selectedContractTerm: selectedValue,
        },
      });
      setAllowServiceRun(true);
    }
  };

  const onSelectVehicleMake = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.currentTarget.value;
    if (selectedValue === "") {
      resetFilter("vehicleMake");
    } else {
      filterDispatch({
        type: "USER_SELECTS_VEHICLE_MAKE",
        payload: {
          selectedVehicleMake: selectedValue,
        },
      });
      setAllowServiceRun(true);
    }
  };

  const onSelectVehicleModel = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedValue = event.currentTarget.value;
    if (selectedValue === "") {
      resetFilter("vehicleModel");
    } else {
      filterDispatch({
        type: "USER_SELECTS_VEHICLE_MODEL",
        payload: {
          selectedVehicleModel: selectedValue,
        },
      });
      setAllowServiceRun(true);
    }
  };

  const onSelectVehicleModelYear = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedValue = event.currentTarget.value;
    if (selectedValue === "") {
      resetFilter("vehicleModelYear");
    } else {
      filterDispatch({
        type: "USER_SELECTS_VEHICLE_MODEL_YEAR",
        payload: {
          selectedVehicleModelYear: selectedValue,
        },
      });
      setAllowServiceRun(true);
    }
  };

  const onSelectDistanceAllowance = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.currentTarget.value;
    if (selectedValue === ""){
      resetFilter("distanceAllowance");
    } else {
      filterDispatch({
        type: "USER_SELECTS_DISTANCE_ALLOWANCE",
        payload: {
          selectedDistanceAllowance: selectedValue,
        }
      });
      setAllowServiceRun(true);
    }
  };

  const onSelectInitialContribution = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.currentTarget.value;
    if (selectedValue === "") {
      resetFilter("initialContribution");
    } else {
      filterDispatch({
        type: "USER_SELECTS_INITIAL_CONTRIBUTION",
        payload: {
          selectedInitialContribution: selectedValue,
        }
      });
      pricingRecordDispatch({ type: "SET_PRICING_RECORD", payload: null });
      setAllowServiceRun(true);
    }
  };

  const onSelectBalloonPayment = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.currentTarget.value;
    if (selectedValue === "") {
      resetFilter("balloonPayment");
    } else {
      filterDispatch({
        type: "USER_SELECTS_BALLOON_PAYMENT",
        payload: {
          selectedBalloonPayment: selectedValue,
        }
      });
      pricingRecordDispatch({ type: "SET_PRICING_RECORD", payload: null });
      setAllowServiceRun(true);
    }
  };

  const onSelectVehicleSpecifier = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.currentTarget.value;
    if (selectedValue === "") {
      resetFilter("vehicleSpecifier");
    } else {
      filterDispatch({
        type: "USER_SELECTS_VEHICLE_SPECIFIER",
        payload: {
          selectedVehicleSpecifier: selectedValue,
        }
      });
      pricingRecordDispatch({ type: "SET_PRICING_RECORD", payload: null });
      setAllowServiceRun(true);
    }
  };

  /**
   * Reset handler that resets a filter and all its dependents
   */
  const resetFilter = (filterType: "contractTerm" | "vehicleMake" | "vehicleModel" | "vehicleModelYear" | "distanceAllowance" | "initialContribution" | "balloonPayment" | "vehicleSpecifier") => {
    const filterConfigs = {
      contractTerm: {
        action: {
          type: "USER_SELECTS_CONTRACT_TERM",
          payload: { selectedContractTerm: "" },
        },
        dependentActions: [
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_MAKE) ?
          [{
            type: "USER_SELECTS_VEHICLE_MAKE",
            payload: { selectedVehicleMake: "" },
          }] : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_MODEL) ?
          [{
            type: "USER_SELECTS_VEHICLE_MODEL",
            payload: { selectedVehicleModel: "" },
          }] : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_MODEL_YEAR) ?
          [{
            type: "USER_SELECTS_VEHICLE_MODEL_YEAR",
            payload: { selectedVehicleModelYear: "" },
          }] : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.DISTANCE_ALLOWANCE)
          ? [{
            type: "USER_SELECTS_DISTANCE_ALLOWANCE",
            payload: {selectedDistanceAllowance: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.INITIAL_CONTRIBUTION)
          ? [{
            type: "USER_SELECTS_INITIAL_CONTRIBUTION",
            payload: {selectedInitialContribution: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.BALLOON_PAYMENT)
          ? [{
            type: "USER_SELECTS_BALLOON_PAYMENT",
            payload: {selectedBalloonPayment: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_SPECIFIER)
          ? [{
            type: "USER_SELECTS_VEHICLE_SPECIFIER",
            payload: {selectedVehicleSpecifier: ""}
          }]
          : [])
        ],
      },
      vehicleMake: {
        action: {
          type: "USER_SELECTS_VEHICLE_MAKE",
          payload: { selectedVehicleMake: "" },
        },
        dependentActions: [
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_MODEL) ?
          [{
            type: "USER_SELECTS_VEHICLE_MODEL",
            payload: { selectedVehicleModel: "" },
          }] : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_MODEL_YEAR) ?
          [{
            type: "USER_SELECTS_VEHICLE_MODEL_YEAR",
            payload: { selectedVehicleModelYear: "" },
          }] : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.DISTANCE_ALLOWANCE)
          ? [{
            type: "USER_SELECTS_DISTANCE_ALLOWANCE",
            payload: {selectedDistanceAllowance: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.INITIAL_CONTRIBUTION)
          ? [{
            type: "USER_SELECTS_INITIAL_CONTRIBUTION",
            payload: {selectedInitialContribution: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.BALLOON_PAYMENT)
          ? [{
            type: "USER_SELECTS_BALLOON_PAYMENT",
            payload: {selectedBalloonPayment: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_SPECIFIER)
          ? [{
            type: "USER_SELECTS_VEHICLE_SPECIFIER",
            payload: {selectedVehicleSpecifier: ""}
          }]
          : [])
        ],
      },
      vehicleModel: {
        action: {
          type: "USER_SELECTS_VEHICLE_MODEL",
          payload: { selectedVehicleModel: "" },
        },
        dependentActions: [
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_MODEL_YEAR) ?
          [{
            type: "USER_SELECTS_VEHICLE_MODEL_YEAR",
            payload: { selectedVehicleModelYear: "" },
          }] : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.DISTANCE_ALLOWANCE)
          ? [{
            type: "USER_SELECTS_DISTANCE_ALLOWANCE",
            payload: {selectedDistanceAllowance: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.INITIAL_CONTRIBUTION)
          ? [{
            type: "USER_SELECTS_INITIAL_CONTRIBUTION",
            payload: {selectedInitialContribution: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.BALLOON_PAYMENT)
          ? [{
            type: "USER_SELECTS_BALLOON_PAYMENT",
            payload: {selectedBalloonPayment: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_SPECIFIER)
          ? [{
            type: "USER_SELECTS_VEHICLE_SPECIFIER",
            payload: {selectedVehicleSpecifier: ""}
          }]
          : [])
        ],
      },
      vehicleModelYear: {
        action: {
          type: "USER_SELECTS_VEHICLE_MODEL_YEAR",
          payload: { selectedVehicleModelYear: "" },
        },
        dependentActions: [
          ...(filterState?.validFilters.includes(MasterSheetFilter.DISTANCE_ALLOWANCE)
          ? [{
            type: "USER_SELECTS_DISTANCE_ALLOWANCE",
            payload: {selectedDistanceAllowance: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.INITIAL_CONTRIBUTION)
          ? [{
            type: "USER_SELECTS_INITIAL_CONTRIBUTION",
            payload: {selectedInitialContribution: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.BALLOON_PAYMENT)
          ? [{
            type: "USER_SELECTS_BALLOON_PAYMENT",
            payload: {selectedBalloonPayment: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_SPECIFIER)
          ? [{
            type: "USER_SELECTS_VEHICLE_SPECIFIER",
            payload: {selectedVehicleSpecifier: ""}
          }]
          : [])
        ],
      },
      distanceAllowance: {
        action: {
          type: "USER_SELECTS_DISTANCE_ALLOWANCE",
          payload: { selectedDistanceAllowance: "" },
        },
        dependentActions: [
          ...(filterState?.validFilters.includes(MasterSheetFilter.INITIAL_CONTRIBUTION)
          ? [{
            type: "USER_SELECTS_INITIAL_CONTRIBUTION",
            payload: {selectedInitialContribution: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.BALLOON_PAYMENT)
          ? [{
            type: "USER_SELECTS_BALLOON_PAYMENT",
            payload: {selectedBalloonPayment: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_SPECIFIER)
          ? [{
            type: "USER_SELECTS_VEHICLE_SPECIFIER",
            payload: {selectedVehicleSpecifier: ""}
          }]
          : [])
        ],
      },
      initialContribution: {
        action: {
          type: "USER_SELECTS_INITIAL_CONTRIBUTION",
          payload: { selectedInitialContribution: "" },
        },
        dependentActions: [
          ...(filterState?.validFilters.includes(MasterSheetFilter.BALLOON_PAYMENT)
          ? [{
            type: "USER_SELECTS_BALLOON_PAYMENT",
            payload: {selectedBalloonPayment: ""}
          }]
          : []),
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_SPECIFIER)
          ? [{
            type: "USER_SELECTS_VEHICLE_SPECIFIER",
            payload: {selectedVehicleSpecifier: ""}
          }]
          : [])
        ],
      },
      balloonPayment: {
        action: {
          type: "USER_SELECTS_BALLOON_PAYMENT",
          payload: { selectedBalloonPayment: "" },
        },
        dependentActions: [
          ...(filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_SPECIFIER)
          ? [{
            type: "USER_SELECTS_VEHICLE_SPECIFIER",
            payload: {selectedVehicleSpecifier: ""}
          }]
          : [])
        ],
      },
      vehicleSpecifier: {
        action: {
          type: "USER_SELECTS_VEHICLE_SPECIFIER",
          payload: { selectedVehicleSpecifier: "" },
        },
        dependentActions: [],
      },
    };

    const config = filterConfigs[filterType];

    // Reset the target filter
    filterDispatch(config.action as SelectedVehicleAction);

    // Reset all dependent filters
    config.dependentActions.forEach((action) =>
        filterDispatch(action as SelectedVehicleAction)
    );

    // Clear pricing record to allow API to be called again
    pricingRecordDispatch({
      type: "SET_PRICING_RECORD",
      payload: null,
    });

    setAllowServiceRun(true);
  };

  /**
   * On submit handler
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(navigateTarget);
  };

  /**
   * Render the component
   */
  return (
    <>
      <NavBar pageTitle="Create Sales Quote" />
      {loading === true && <ForegroundLoadingSpinner />}
      <form style={{ margin: "2.5vh 2.5vw" }} onSubmit={handleSubmit}>
        {/* Customer Details Section */}
        <Section
          childCmp={
            <>
              <p>Customer Details</p>
              <div style={style} data-testid={"customerDetailsPanel"}>
                {customerDetails.map((itemProps, index) => (
                  <PanelItem
                    key={`customerDetailsPanelItem-${index}`}
                    {...itemProps}
                  />
                ))}
              </div>
            </>
          }
        />

        {/* Vehicle Selection Section */}
        <Section
          childCmp={
            <>
              <p>Please specify the following attributes to choose a plan: </p>
              {/* TODO: [FT2.0] Replace the following with a reusable component so we can have a conditional rendering for FO/FT here 
              * 1. Create FilterDropdownComponent
              * 2. Use it to render the dropdowns below
              * 3. Create separate components for FO2.0 and FT2.0 filters
              * 4. Use conditional rendering to render the correct component based on selected plan
              */}
              { filterState?.validFilters.includes(MasterSheetFilter.CONTRACT_TERM) &&
                <div style={{ margin: "2.5vh 0vw" }}>
                  <Label text="Term Length (year)" requiredFieldInd={true} />
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <DropDownMenu
                      key={`contractTerm-${filterState?.selectedContractTerm}`}
                      menuName="contractTerm"
                      required={true}
                      choices={(
                        filterOptionsState?.term_length || []
                      ).map((item: IFilterOption) => ({
                        label: item.description || item.id,
                        value: item.id,
                        enabled: true,
                      }))}
                      defaultVal={filterState?.selectedContractTerm !== "" ? { value: filterState?.selectedContractTerm } : undefined}
                      onSelect={onSelectContractTerm}
                      testId="contractTerm"
                      disabled={filterState?.selectedContractTerm !== "" || loading === true}
                    />
                    {filterState?.selectedContractTerm && (
                      <ResetButton
                        onClick={() => resetFilter("contractTerm")}
                        testId="reset-contract-term"
                      />
                    )}
                  </div>
                </div>
              }
              { filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_MAKE) &&
                filterState?.selectedContractTerm && (
                <div style={{ margin: "2.5vh 0vw" }}>
                  <Label text="Make" requiredFieldInd={true} />
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <DropDownMenu
                      key={`vehicleMake-${filterState?.selectedVehicleMake}`}
                      menuName="vehicleMake"
                      required={true}
                      choices={(filterOptionsState?.make || []).map(
                        (item: IFilterOption) => ({
                          label: item.description || item.id,
                          value: item.id,
                          enabled: true,
                        })
                      )}
                      defaultVal={filterState?.selectedVehicleMake !== "" ? { value: filterState?.selectedVehicleMake } : undefined}
                      onSelect={onSelectVehicleMake}
                      testId="vehicleMake"
                      disabled={filterState.selectedVehicleMake !== "" || loading === true}
                    />
                    {filterState?.selectedVehicleMake && (
                      <ResetButton
                        onClick={() => resetFilter("vehicleMake")}
                        testId="reset-vehicle-make"
                      />
                    )}
                  </div>
                </div>
              )}
              { filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_MODEL) &&
                filterState?.selectedVehicleMake && (
                <div style={{ margin: "2.5vh 0vw" }}>
                  <Label text="Model" requiredFieldInd={true} />
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <DropDownMenu
                      key={`vehicleModel-${filterState?.selectedVehicleModel}`}
                      menuName="vehicleModel"
                      required
                      choices={(
                        filterOptionsState?.model || []
                      ).map((item: IFilterOption) => ({
                        label: item.description || item.id,
                        value: item.id,
                        enabled: true,
                      }))}
                      defaultVal={filterState?.selectedVehicleModel !== "" ? { value: filterState?.selectedVehicleModel } : undefined}
                      onSelect={onSelectVehicleModel}
                      testId="vehicleModel"
                      disabled={filterState.selectedVehicleModel !== "" || loading === true}
                    />
                    {filterState?.selectedVehicleModel && (
                      <ResetButton
                        onClick={() => resetFilter("vehicleModel")}
                        testId="reset-vehicle-model"
                      />
                    )}
                  </div>
                </div>
              )}
              { filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_MODEL_YEAR) &&
                filterState?.selectedVehicleModel && (
                <div style={{ margin: "2.5vh 0vw" }}>
                  <Label text="Model Year" requiredFieldInd={true} />
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <DropDownMenu
                      key={`vehicleModelYear-${filterState?.selectedVehicleModelYear}`}
                      menuName="vehicleModelYear"
                      required
                      choices={(
                        filterOptionsState?.model_year || []
                      ).map((item: IFilterOption) => ({
                        label: item.description || item.id,
                        value: item.id,
                        enabled: true,
                      }))}
                      defaultVal={filterState?.selectedVehicleModelYear !== "" ? { value: filterState?.selectedVehicleModelYear } : undefined}
                      onSelect={onSelectVehicleModelYear}
                      testId="vehicleModelYear"
                      disabled={filterState?.selectedVehicleModelYear !== "" || loading === true}
                    />
                    {filterState?.selectedVehicleModelYear && (
                      <ResetButton
                        onClick={() => resetFilter("vehicleModelYear")}
                        testId="reset-vehicle-model-year"
                      />
                    )}
                  </div>
                </div>
              )}
              { filterState?.validFilters.includes(MasterSheetFilter.DISTANCE_ALLOWANCE) &&
                filterState?.selectedVehicleModelYear && (
                <div style={{ margin: "2.5vh 0vw" }}>
                  <Label text="Weekly Distance Allowance" requiredFieldInd={true}/>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <DropDownMenu
                      key={`distanceAllowance-${filterState?.selectedDistanceAllowance}`}
                      menuName="distanceAllowance"
                      required
                      choices={(
                        filterOptionsState?.distance_allowance || []
                      ).map((item: IFilterOption) => ({
                        label: item.description || item.id,
                        value: item.id,
                        enabled: true
                      }))}
                      defaultVal={filterState?.selectedDistanceAllowance !== "" ? {value: filterState.selectedDistanceAllowance} : undefined}
                      onSelect={onSelectDistanceAllowance}
                      testId="distanceAllowance"
                      disabled={filterState?.selectedDistanceAllowance !== "" || loading === true}
                    />
                    {filterState?.selectedDistanceAllowance && (
                      <ResetButton
                        onClick={() => resetFilter("distanceAllowance")}
                        testId="reset-distance-allowance"
                      />
                    )}
                    </div>
                </div>
                )
              }
              { foQuoting21Enabled && filterState?.validFilters.includes(MasterSheetFilter.INITIAL_CONTRIBUTION) &&
                filterState?.selectedDistanceAllowance && (
                <div style={{ margin: "2.5vh 0vw" }}>
                  <Label text="Initial Contribution" requiredFieldInd={true}/>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <DropDownMenu
                      key={`initialContribution-${filterState?.selectedInitialContribution}`}
                      menuName="initialContribution"
                      required
                      choices={(
                        filterOptionsState?.initial_contribution || []
                      ).map((item: IFilterOption) => ({
                        label: item.description || item.id,
                        value: item.id,
                        enabled: true
                      }))}
                      defaultVal={filterState?.selectedInitialContribution !== "" ? {value: filterState.selectedInitialContribution} : undefined}
                      onSelect={onSelectInitialContribution}
                      testId="initialContribution"
                      disabled={filterState?.selectedInitialContribution !== "" || loading === true}
                    />
                    {filterState?.selectedInitialContribution && (
                      <ResetButton
                        onClick={() => resetFilter("initialContribution")}
                        testId="reset-initial-contribution"
                      />
                    )}
                  </div>
                </div>
                )
              }
              {/* Vehicle specifier (POFO per-vehicle pricing). Optional: left untouched,
                the server prices the generic ('0') plan, so non-POFO quoting is unchanged.
                Once per-vehicle plans exist for the selected combination, no pricing record
                is generated until a value (a specifier or "None") is chosen here. */}
              { foQuoting21Enabled && vehicleSpecifierQuotingEnabled &&
                filterState?.validFilters.includes(MasterSheetFilter.VEHICLE_SPECIFIER) &&
                filterState?.selectedInitialContribution && (
                <div style={{ margin: "2.5vh 0vw" }}>
                  <Label text="Specific Vehicle" requiredFieldInd={false}/>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <DropDownMenu
                      key={`vehicleSpecifier-${filterState?.selectedVehicleSpecifier}`}
                      menuName="vehicleSpecifier"
                      required={false}
                      choices={(
                        filterOptionsState?.vehicle_specifier || []
                      ).map((item: IFilterOption) => ({
                        label: item.description || item.id,
                        value: item.id,
                        enabled: true
                      }))}
                      defaultVal={filterState?.selectedVehicleSpecifier !== "" ? {value: filterState.selectedVehicleSpecifier} : undefined}
                      onSelect={onSelectVehicleSpecifier}
                      testId="vehicleSpecifier"
                      disabled={filterState?.selectedVehicleSpecifier !== "" || loading === true}
                    />
                    {filterState?.selectedVehicleSpecifier && (
                      <ResetButton
                        onClick={() => resetFilter("vehicleSpecifier")}
                        testId="reset-vehicle-specifier"
                      />
                    )}
                  </div>
                </div>
                )
              }
              {/* Sold-vehicle indicator (TP-6846): non-blocking advisory shown once a specific
                vehicle is selected, keyed on the vehicle_specifier. '0' = not vehicle-specific. */}
              { foQuoting21Enabled &&
                filterState?.selectedVehicleSpecifier &&
                filterState.selectedVehicleSpecifier !== '0' && (
                <VehicleQuoteUsageIndicator
                  country={filterState.selectedCountry}
                  vehicleSpecifier={filterState.selectedVehicleSpecifier}
                />
                )
              }
              {/* TODO: Balloon payment is temporarily hidden. Remove the comment tags below to re-enable.
              { foQuoting21Enabled && filterState?.validFilters.includes(MasterSheetFilter.BALLOON_PAYMENT) &&
                filterState?.selectedInitialContribution && (
                <div style={{ margin: "2.5vh 0vw" }}>
                  <Label text="Balloon Payment" requiredFieldInd={true}/>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <DropDownMenu
                      key={`balloonPayment-${filterState?.selectedBalloonPayment}`}
                      menuName="balloonPayment"
                      required
                      choices={(
                        filterOptionsState?.balloon_payment || []
                      ).map((item: IFilterOption) => ({
                        label: item.description || item.id,
                        value: item.id,
                        enabled: true
                      }))}
                      defaultVal={filterState?.selectedBalloonPayment !== "" ? {value: filterState.selectedBalloonPayment} : undefined}
                      onSelect={onSelectBalloonPayment}
                      testId="balloonPayment"
                      disabled={filterState?.selectedBalloonPayment !== "" || loading === true}
                    />
                    {filterState?.selectedBalloonPayment && (
                      <ResetButton
                        onClick={() => resetFilter("balloonPayment")}
                        testId="reset-balloon-payment"
                      />
                    )}
                  </div>
                </div>
                )
              }
              */}
            </>
          }
        />
        {/* Chargebee Plan Details Section */}
        {pricingRecordState && allRequiredFiltersSelected && filterState?.selectedContractTerm && filterState?.selectedVehicleMake && filterState?.selectedVehicleModel && (
          <>
            <Section
              childCmp={
                <>
                  <Divider />
                  <Label text="Plan" />
                  {/* Disabled input as this is just to show the selected plan */}
                  <input
                    type="text"
                    value={
                      getLineItemByLabelCode(pricingRecordState, "A1")
                        ?.chargebee_item_name || ""
                    }
                    readOnly
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "5px",
                      border: "1px solid #ccc",
                    }}
                    data-testid="planInput"
                  />

                {/* Show warnings if there are any errors from pricing record or error state */}
                {(errorState.hasError) && (
                    <Warnings
                      errors={errorState.errors}
                    />
                  )}

                  <Divider />

                  <InfoBox message={
                    <>
                      All Prices are listed as <strong>TAX Inclusive</strong> where applicable.
                    </>
                  } />
                  {/* Price Breakdown */}
                  <PriceBreakdownComponent
                    pricingRecord={pricingRecordState}
                    showTotalWeeklySubscriptionPrice={true}
                    showSetupFee={false}
                  />
                </>
              }
            />
          </>
        )}
        {/* Warnings raised without a pricing record (e.g. a complete selection that matches no plan).
        Warnings that come with a pricing record are shown in the plan section above instead. */}
        {errorState.hasError && !pricingRecordState && (
          <Warnings
            errors={errorState.errors}
          />
        )}
        {/* Submit Button */}
        {/* This button is disabled when there is no pricing record found after
        the user selects all the filters OR there are errors in the pricing record
        (so that the user can report the error through ITS and the team can help fix
        from the backend) */}
        <SubmitBtn
          text="Next"
          disabled={
            !pricingRecordState ||
            !allRequiredFiltersSelected ||
            (pricingRecordState && errorState.hasError)
          }
        />
      </form>
    </>
  );
}

/**
 * Helper components
 */
const Section = ({ childCmp }: { childCmp: JSX.Element }) => (
  <div style={{ margin: "2.5vh 0vw" }}>{childCmp}</div>
);

const Divider = () => (
  <hr style={{ margin: "2vh 0", border: "none", borderTop: "1px solid #e0e0e0" }} />
);
